import { preflightStaffIngress } from "@/services/staffIngress";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  useImperativeHandle,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Tooltip } from "@mui/material";
import { Menu } from "@/components/FpPages/RMS/design";
import MenuTriggerTooltip from "@/components/FpPages/RMS/design/MenuTriggerTooltip";
import { hasSoftKeyboard } from "@/utils/softKeyboard";
import { visibleChosenFiles } from "@/hooks/useFileTreeV2/upload";
import { DotsThreeIcon, UploadSimpleIcon } from "@phosphor-icons/react";
import EntityIcon from "@/components/FpPages/RMS/Dashboard/Components/EntityIcon";
import { recordIconKind } from "@/components/FpPages/Documents/browser/records";
import { useDocumentRecord } from "@/components/FpPages/Documents/browser/useDocumentsSearch";
import { useSelector } from "react-redux";
import {
  useFileSelection,
  expandSelectionsToFileIds,
  ExpandedFileInfo,
  FolderSelection,
} from "@/hooks/useFileTreeV2";
import { useDriveSelector } from "@/hooks/useFileTreeV2/useDriveSelector";
import { usePlaces } from "@/components/FpPages/Documents/browser/useDocumentsSearch";
import { placeFor } from "@/components/FpPages/Documents/browser/places";
import { displayFileName } from "@/utils/fileName";
import {
  placeRowItems,
  type PlaceRowTarget,
} from "@/components/FpPages/Documents/browser/PlaceRows";
import LocationChip from "@/components/FpPages/Documents/LocationChip";
import type { FolderEntry } from "@/hooks/useFileTreeV2/fetchFiles";
import { FileSelectionModalProps, UploadInfo } from "./types";
import type { PickedUploadInfo } from "./uploadIdentity";
import { uploadFile } from "@/components/common/FileUploadV3";
import { useIsPhoneViewport } from "@/hooks/useViewportTier";
import useOrgId from "@/components/FpAppV4/hooks/useOrgId";
import { queryClient } from "@/ReactQuery/reactQueryClient";
import DriveSelectionModal from "./DriveSelectionModal";
import DocumentsBrowser from "@/components/FpPages/Documents/browser/DocumentsBrowser";
import Breadcrumbs from "@/components/FpPages/Documents/browser/Breadcrumbs";
import {
  searchDocuments,
  type DocumentRow,
} from "@/components/FpPages/Documents/browser/api";
import {
  BrowserState,
  DEFAULT_BROWSER_STATE,
  VIEW_LABELS,
  hasActiveFilters,
  openedFolder,
  openedRecord,
} from "@/components/FpPages/Documents/browser/state";
import useDismissOnBack from "@/hooks/useDismissOnBack";

/**
 * A reusable modal for file selection with folder expansion support.
 *
 * This component handles:
 * - Modal structure (overlay, header, footer)
 * - File selection via useFileSelection hook
 * - Folder expansion via expandSelectionsToFileIds (ensures folder selections are expanded to actual file IDs)
 * - Confirm/cancel button logic
 *
 * Custom features (upload zones, AI filtering, etc.) can be passed via named slots.
 *
 * @example
 * ```tsx
 * <FileSelectionModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onConfirm={(fileIds) => handleFilesSelected(fileIds)}
 *   title="Add Documents"
 *   confirmButtonText="Save"
 *   beforeTreeSlot={<UploadZone onUpload={handleUpload} />}
 * />
 * ```
 */

const DEFAULT_EXCLUDE_BLOB_KINDS = ["unsupported", "unreadable"];
const DEFAULT_CONFIRM = "Select";

const samePath = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((s, i) => s === b[i]);
const sameFolder = (a: FolderSelection, b: FolderSelection): boolean =>
  (a.driveId ?? null) === (b.driveId ?? null) && samePath(a.path, b.path);
const inFolder = (filePath: string[], folder: string[]): boolean =>
  filePath.length >= folder.length && folder.every((s, i) => filePath[i] === s);

interface RmsProductState {
  userData?: { organization?: { allowed_products?: string[] } };
}

const FileSelectionModal: React.FC<FileSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onConfirmWithSelection,
  // One title and one verb for every host (Claude Design, round 8): the
  // host's own context already says what the documents are for, and the
  // modal adds nothing — the host does, after it returns.
  title = "Select documents",
  titleSlot,
  autoFocusSearch = true,
  confirmButtonText = DEFAULT_CONFIRM,
  confirmButtonSlot,
  selectionRef,
  clearSelectionOnClose = true,
  initialFileIds,
  initialFolderPaths,
  onFileClick,
  isPending = false,
  allowEmptySelection = false,
  headerSlot,
  beforeTreeSlot,
  afterTreeSlot,
  footerSlot,
  widthClass = "w-3/5 max-w-4xl",
  heightClass = "h-4/5",
  excludeBlobKinds = DEFAULT_EXCLUDE_BLOB_KINDS,
  onSelectionChange,
  enableUpload = false,
  onUploadStarting,
  onUploadComplete,
  onUploadFailed,
  reuseDuplicateUploads = false,
}) => {
  const [expandError, setExpandError] = useState<string | null>(null);
  const [isExpanding, setIsExpanding] = useState(false);

  // One picker (#6559 round 5, Claude Design): the documents browser in
  // selection mode is the whole body. Folder rows drill in, the trail comes
  // back under the title, a folder row is tickable, and the chips are the
  // ones a selection surface earns.
  const [findState, setFindState] = useState<BrowserState>(
    DEFAULT_BROWSER_STATE
  );
  // The title row's right side hosts the listing's pinned group — Clear
  // filters, then the count — so the chips row carries only controls
  // (Claude Design, round 9). Held in state so the browser can portal into
  // it once it exists.
  const [pinnedHost, setPinnedHost] = useState<HTMLDivElement | null>(null);
  // A record chosen from the search is the picker's location (round 10):
  // resolved here for its name and icon on the trail, and the listing waits
  // for it the way the page's does.
  const onRecordView = findState.view === "record";
  const record = useDocumentRecord(onRecordView ? findState.record : null);
  // The places (round 17, Claude Design ruling D): with drives on the root
  // lists them as rows and a location chip under the title names where the
  // reader is and jumps between them; with drives off the root is the tree's
  // and the chip is how a connection is reached.
  const places = usePlaces(isOpen);
  const currentPlace = placeFor(places.data, findState.drive, findState.path);
  const atPlacesRoot =
    places.data?.drivesEnabled === true &&
    findState.view === "all" &&
    findState.drive === null &&
    findState.path.length === 0 &&
    !hasActiveFilters(findState);
  const widestLabel = places.data?.drivesEnabled
    ? "All drives and apps"
    : "All documents";
  const goToPlace = (target: PlaceRowTarget) => {
    setShowSelectedOnly(false);
    beforeRecordRef.current = null;
    setFindState((prev) =>
      target.view
        ? { ...openedFolder(prev, []), view: target.view, drive: null }
        : { ...openedFolder(prev, target.path), drive: target.driveId }
    );
  };
  // Drive's shape (round 18): the root, My Drive, the Shared drives listing
  // (which drills into the drives), then the connections themselves.
  const locationItems = places.data
    ? [
        {
          key: "root",
          label: widestLabel,
          onSelect: () => goToPlace({ driveId: null, path: [] }),
        },
        ...placeRowItems(places.data, "root").map((item) => ({
          key: item.key,
          label: item.label,
          onSelect: () => goToPlace(item.target),
        })),
      ]
    : [];
  const onPlacesListing = findState.view === "drives";
  const locationLabel = atPlacesRoot
    ? widestLabel
    : onPlacesListing
      ? VIEW_LABELS[findState.view]
      : currentPlace.label;
  // Nothing to jump between: one tree and no app connected.
  const showLocationChip =
    places.data !== undefined &&
    (places.data.drivesEnabled || places.data.connectedApps.length > 0);
  const awaitingRecord = onRecordView && !record.isSuccess && !record.isError;
  const rmsEnabled = useSelector(
    (state: RmsProductState) =>
      state.userData?.organization?.allowed_products?.includes("crm") ?? false
  );

  // Upload-related state
  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[] | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  // The folder dialog is a second input: browsers make the user choose one
  // dialog or the other (round 10).
  const folderInputRef = useRef<HTMLInputElement>(null);
  // The Upload menu's trigger, and which item takes focus on open: a
  // trigger opened with ArrowUp asks for the last (round 13).
  const [uploadMenu, setUploadMenu] = useState<{
    anchor: HTMLElement;
    focus: "first" | "last";
  } | null>(null);
  const orgId = useOrgId();

  // Get can_upload flag from Redux state
  const userData = useSelector((state: any) => state.userData);
  const canUpload = userData?.can_upload !== false;

  // Phone swaps the caller-supplied desktop width/height for the one phone
  // dialog shape (#11742, CD (b)).
  const phone = useIsPhoneViewport();

  // Every host mounts its picker closed on page load, so a closed picker
  // loads nothing: no tree, no drive list (#12239). Latched, not following
  // `isOpen`: the selection hook stays mounted across a close (a host with
  // clearSelectionOnClose={false} keeps its selection), and a reopen should
  // not wait for data it already has.
  const [hasOpened, setHasOpened] = useState(isOpen);
  useEffect(() => {
    if (isOpen) setHasOpened(true);
  }, [isOpen]);

  const {
    selectedFileIds,
    selectedFiles,
    selectedFileCount,
    clearSelection: clearFileSelection,
    setSelection: setFileSelection,
    selectFiles,
  } = useFileSelection({
    initialFileIds,
    onFileClick,
    excludeBlobKinds,
    loadTree: hasOpened,
  });
  // "Show selected" is the picker's own (#12088): the selection hook's copy
  // stands down the moment its file selection is empty, and it knows
  // nothing of the folder picks below, so a selection of folders alone
  // (two folder hits ticked from a search) could never be shown.
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  // The drives an upload can land in. The tree used to learn these from its
  // root load; with the tree gone the picker asks for them itself.
  const { drives, drivesEnabled } = useDriveSelector({ enabled: hasOpened });

  // Folder selections are the modal's own, each with the drive it was ticked
  // in: the tree's plumbing carried the drive only through its own rows. A
  // host hands folders in as paths, and a path whose first segment names one
  // of the organization's drives carries that drive (the encoding the tree's
  // own parser wrote whenever a folder had one); the decode waits for the
  // drives to be known, so a selection applied before they load still
  // resolves.
  const [tickedFolders, setTickedFolders] = useState<FolderSelection[]>([]);
  const [handedFolderPaths, setHandedFolderPaths] = useState<string[][]>([]);
  const decodeHandedFolder = useCallback(
    (path: string[]): FolderSelection => {
      const head = path[0];
      return drivesEnabled &&
        path.length > 1 &&
        head !== undefined &&
        drives.some((d) => d._id === head)
        ? { driveId: head, path: path.slice(1) }
        : { driveId: null, path };
    },
    [drives, drivesEnabled]
  );
  const folderSelections = useMemo<FolderSelection[]>(
    () => [...handedFolderPaths.map(decodeHandedFolder), ...tickedFolders],
    [handedFolderPaths, tickedFolders, decodeHandedFolder]
  );
  const selectedFolderCount = folderSelections.length;
  const setSelection = (fileIds: string[], folderPaths: string[][] = []) => {
    setFileSelection(fileIds);
    setHandedFolderPaths(folderPaths);
    setTickedFolders([]);
  };
  // Stable, and a no-op on an already-empty selection: the close effect
  // below depends on it, and a fresh callback writing fresh empties every
  // render sent every closed picker into a render loop.
  const clearSelection = useCallback(() => {
    clearFileSelection();
    setTickedFolders((prev) => (prev.length ? [] : prev));
    setHandedFolderPaths((prev) => (prev.length ? [] : prev));
    setPathsById((prev) => (prev.size ? new Map() : prev));
  }, [clearFileSelection]);

  // The browser ticks files by id; a tick on a file the tree has not loaded
  // becomes a pending selection carrying the row's name and kind, which is
  // exactly what `selectFiles` does for a search hit. A folder tick and the
  // documents inside it are mutually exclusive within that folder, so every
  // selected document's path is known: a tick records it, and a selection
  // that arrived some other way (the host's initial ids, its ref) is looked
  // up by id.
  const selectedIdSet = new Set(selectedFileIds);
  // A place of null is an id the lookup could not place — a document since
  // deleted, or outside what the viewer can see — which withholds nothing.
  const [pathsById, setPathsById] = useState(
    new Map<string, { driveId: string | null; path: string[] } | null>()
  );
  const unresolvedIds = useMemo(
    () => selectedFileIds.filter((id) => !pathsById.has(id)),
    [selectedFileIds, pathsById]
  );
  const resolved = useQuery({
    queryKey: ["fileTree", "picker", "paths", unresolvedIds],
    queryFn: () =>
      searchDocuments({
        blob_ids: unresolvedIds,
        path: [],
        recursive: true,
        sort_by: "name",
        sort_order: "asc",
        page_size: unresolvedIds.length,
      }),
    enabled: isOpen && unresolvedIds.length > 0,
    staleTime: Infinity,
  });
  const askedIds = unresolvedIds;
  useEffect(() => {
    if (!resolved.data) return;
    setPathsById((prev) => {
      const next = new Map(prev);
      for (const id of askedIds) if (!next.has(id)) next.set(id, null);
      for (const row of resolved.data.files)
        next.set(row.fileId, { driveId: row.driveId, path: row.filePath });
      return next;
    });
    // The ids the answered lookup was asked for are the ones it settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved.data]);
  const removeFile = (fileId: string) => {
    setFileSelection(selectedFileIds.filter((id) => id !== fileId));
    setPathsById((prev) => {
      const next = new Map(prev);
      next.delete(fileId);
      return next;
    });
  };
  // Off, whichever way it arrived.
  const removeFolder = (pick: FolderSelection) => {
    setTickedFolders((prev) => prev.filter((f) => !sameFolder(f, pick)));
    setHandedFolderPaths((prev) =>
      prev.filter((path) => !sameFolder(decodeHandedFolder(path), pick))
    );
  };
  const findSelection = {
    isSelected: (fileId: string) => selectedIdSet.has(fileId),
    toggle: (row: DocumentRow) => {
      if (selectedIdSet.has(row.fileId)) {
        removeFile(row.fileId);
      } else {
        setPathsById((prev) =>
          new Map(prev).set(row.fileId, {
            driveId: row.driveId,
            path: row.filePath,
          })
        );
        selectFiles(
          [row.fileId],
          new Map([
            [
              row.fileId,
              { name: displayFileName(row.fileName), type: row.fileType },
            ],
          ])
        );
      }
    },
    isFolderSelected: (folder: FolderEntry) =>
      folderSelections.some((f) =>
        sameFolder(f, { driveId: folder.driveId, path: folder.filePath })
      ),
    toggleFolder: (folder: FolderEntry) => {
      const pick = { driveId: folder.driveId, path: folder.filePath };
      if (folderSelections.some((f) => sameFolder(f, pick))) {
        removeFolder(pick);
      } else {
        setTickedFolders((prev) => [...prev, pick]);
      }
    },
    // A folder cannot be ticked over a document ticked inside it. Until
    // every selected document's place is known — the lookup still running,
    // or failed — no folder is tickable: the rule is not quietly dropped.
    canSelectFolder: (folder: FolderEntry) =>
      unresolvedIds.length === 0 &&
      !selectedFileIds.some((id) => {
        const place = pathsById.get(id);
        return (
          place !== undefined &&
          place !== null &&
          (place.driveId ?? null) === (folder.driveId ?? null) &&
          inFolder(place.path, folder.filePath)
        );
      }),
    // Select all (round 10): every listed selectable file at once, or off.
    setAll: (rowsToSet: DocumentRow[], selected: boolean) => {
      if (!isOpenRef.current) return;
      if (selected) {
        const fresh = rowsToSet.filter((row) => !selectedIdSet.has(row.fileId));
        if (fresh.length === 0) return;
        setPathsById((prev) => {
          const next = new Map(prev);
          for (const row of fresh)
            next.set(row.fileId, { driveId: row.driveId, path: row.filePath });
          return next;
        });
        selectFiles(
          fresh.map((row) => row.fileId),
          new Map(
            fresh.map((row) => [
              row.fileId,
              { name: displayFileName(row.fileName), type: row.fileType },
            ])
          )
        );
      } else {
        const off = new Set(rowsToSet.map((row) => row.fileId));
        setFileSelection(selectedFileIds.filter((id) => !off.has(id)));
        setPathsById((prev) => {
          const next = new Map(prev);
          for (const id of off) next.delete(id);
          return next;
        });
      }
    },
    canSelectFile: (row: DocumentRow) =>
      !folderSelections.some(
        (f) =>
          (f.driveId ?? null) === (row.driveId ?? null) &&
          inFolder(row.filePath, f.path)
      ),
    // The folder that selected the row, by name, so the row can say so
    // (#12088, Hunter): a bare disabled tick under a ticked folder read as
    // a document that could not be chosen at all.
    coveringFolder: (row: DocumentRow) => {
      const pick = folderSelections.find(
        (f) =>
          (f.driveId ?? null) === (row.driveId ?? null) &&
          inFolder(row.filePath, f.path)
      );
      return pick ? (pick.path[pick.path.length - 1] ?? null) : null;
    },
  };
  // The phone sheet is a pushed state: back closes it.
  useDismissOnBack(phone && isOpen, onClose);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const openFindFile = (row: DocumentRow) =>
    onFileClick?.({
      fileId: row.fileId,
      fileName: row.fileName,
      fileType: row.fileType,
      filePath: row.filePath,
    });

  // Expose selection controls via ref for external management
  useImperativeHandle(
    selectionRef,
    () => ({
      setSelection,
      selectFiles,
      clearSelection,
      getSelection: () => ({
        fileIds: selectedFileIds,
        folderSelections: folderSelections,
      }),
      setShowSelectedOnly,
    }),
    [
      setSelection,
      selectFiles,
      clearSelection,
      selectedFileIds,
      folderSelections,
      setShowSelectedOnly,
    ]
  );

  // Clear selection and error when modal closes (if configured)
  useEffect(() => {
    if (!isOpen) {
      if (clearSelectionOnClose) {
        clearSelection();
      }
      setExpandError(null);
      setIsExpanding(false);
      setPendingUploadFiles(null);
    }
  }, [isOpen, clearSelection, clearSelectionOnClose]);

  // Re-apply the initial selection when the modal reopens; the listing
  // itself is reset on close (below).
  const prevIsOpenRef = useRef(false);
  // What the reader was looking at before a record took over (round 11):
  // opening a record clears the term so the record's documents can be
  // searched, and the root crumb brings it — and the chips — back. Declared
  // here, above the early returns, like every hook.
  const beforeRecordRef = useRef<BrowserState | null>(null);
  // Read by callbacks that outlive a render (a select-all run still paging
  // when the modal closes): a closed picker takes no selection.
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      const hasInitial =
        (initialFileIds?.length ?? 0) > 0 ||
        (initialFolderPaths?.length ?? 0) > 0;
      if (hasInitial)
        setSelection(initialFileIds ?? [], initialFolderPaths ?? []);
    }
    prevIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Handler for when files are selected from the filesystem
  const handleFilesSelected = (files: File[]) => {
    if (!canUpload) {
      return;
    }

    if (drivesEnabled) {
      // If drives are enabled, show drive selection
      setPendingUploadFiles(files);
    } else {
      // If drives are not enabled, proceed directly to upload
      handleDirectUpload(files);
    }
  };

  // Handler for direct upload when drives are not enabled
  const handleDirectUpload = async (files: File[]) => {
    // Notify parent that uploads are starting
    onUploadStarting?.(files);

    // Close the modal immediately
    onClose();

    try {
      // One staff ingress check for the whole pick (#10718): a refusal
      // the staff member declined fails every file and sends nothing.
      const preflight = await preflightStaffIngress(
        "document",
        files.map((file) => ({
          name: file.webkitRelativePath || file.name,
          file,
        }))
      );
      if (!preflight.proceed) {
        for (const file of files) onUploadFailed?.(file);
        return;
      }
      for (const file of files) {
        try {
          // Every pick is anchored at the root: a file from the folder dialog
          // carries its path relative to the chosen folder, which `uploadFile`
          // appends, so it keeps that structure; a plain file has none.
          const result = await uploadFile({
            orgId: orgId,
            file,
            useFilesystem: true,
            batchSize: files.length,
            baseDirectoryPath: [],
            // Chat attaches, it doesn't file — a document we already hold is
            // handed back rather than refused (#11242).
            reuseDuplicate: reuseDuplicateUploads,
            ingressBatch: preflight.batch,
          });

          // Notify parent that this file upload completed. The answer names
          // the File it is for: two picks can share a name, and matching the
          // chip by name gave both the first blob (#12187).
          if (result.blob_id) {
            const upload: PickedUploadInfo = {
              blobId: result.blob_id,
              taskId: result._id,
              fileName: file.name,
              duplicateOf: result.duplicate_of ?? undefined,
              duplicateFileName: result.filename,
              processed: result.processed,
              processingFailed: result.processing_failed,
              file,
            };
            onUploadComplete?.(upload);
          } else {
            // No blob, no answer to wait for: the host's chip must leave
            // "uploading" or it holds the composer's Send for good (#12187).
            onUploadFailed?.(file);
          }
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          onUploadFailed?.(file);
        }
      }

      // Invalidate queries to refresh the file list
      queryClient.invalidateQueries({ queryKey: ["orgFileList"] });
    } catch (error) {
      console.error("Error uploading files:", error);
    }
  };

  // Handler for file input changes
  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      if (!canUpload) {
        return;
      }
      // Hidden files and hidden directories out; every file type uploads
      // (the #11068 type gate is lifted, 2026-09-28).
      const fileArray = visibleChosenFiles(files);
      if (fileArray.length > 0) {
        handleFilesSelected(fileArray);
      }
    }
    // Reset the input value so the same files can be selected again —
    // whichever of the two inputs answered.
    event.target.value = "";
  };

  // Handler for each drive upload completion
  const handleDriveSelectionComplete = (upload: UploadInfo) => {
    onUploadComplete?.(upload);
  };

  // Handler for when drive upload starts (closes entire modal)
  const handleDriveUploadStarting = (files: File[]) => {
    setPendingUploadFiles(null);
    onUploadStarting?.(files);
    onClose();
  };

  // Handler to go back from drive selection to file tree
  const handleDriveSelectionBack = () => {
    setPendingUploadFiles(null);
  };

  // Clear error when selection changes
  useEffect(() => {
    if (expandError) {
      setExpandError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFileIds.length, folderSelections.length]);

  // The picks are a scope of the listing (round 15, Claude Design), not
  // chips in the footer: the footer is the modal's most constrained space,
  // and a chip per pick made it worse the more the reader chose. The folder
  // picks list as folder rows there, beside the documents.
  const selectedFolderEntries = useMemo<FolderEntry[]>(
    () =>
      folderSelections.map((pick) => ({
        filePath: pick.path,
        documentDate: null,
        driveId: pick.driveId,
      })),
    [folderSelections]
  );
  const selectedCount = selectedFileIds.length + folderSelections.length;
  // With nothing left to list the scope stands down on its own.
  useEffect(() => {
    if (showSelectedOnly && selectedCount === 0) setShowSelectedOnly(false);
  }, [showSelectedOnly, selectedCount, setShowSelectedOnly]);

  // A closed picker forgets its open (#11207): "Show selected" is a way of
  // looking, not a selection, and the search, the filters and the location
  // were that open's — the next one lists from the root with an empty
  // search box.
  useEffect(() => {
    if (!isOpen) {
      setShowSelectedOnly(false);
      setFindState(DEFAULT_BROWSER_STATE);
      beforeRecordRef.current = null;
    }
  }, [isOpen, setShowSelectedOnly]);

  // Notify parent when selection changes
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  useEffect(() => {
    if (isOpen && onSelectionChangeRef.current) {
      onSelectionChangeRef.current(selectedFiles, folderSelections);
    }
  }, [isOpen, selectedFiles, folderSelections]);

  const isConfirmDisabled =
    (!allowEmptySelection &&
      selectedFileCount === 0 &&
      selectedFolderCount === 0) ||
    isPending ||
    isExpanding;

  const handleConfirm = async () => {
    let expandedFiles: ExpandedFileInfo[];
    let processingCount = 0;

    // Expand folder selections to file metadata
    // Always call the API to get proper file names, even for just file IDs
    // Also call when some selected files have empty names (e.g. pending files from search)
    const hasFilesWithoutNames = selectedFiles.some((f) => !f.name);
    if (
      folderSelections.length > 0 ||
      (selectedFileIds.length > 0 &&
        (selectedFiles.length === 0 || hasFilesWithoutNames))
    ) {
      setIsExpanding(true);
      try {
        const result = await expandSelectionsToFileIds(
          selectedFileIds,
          folderSelections,
          excludeBlobKinds
        );
        expandedFiles = result.files;
        processingCount = result.processingCount;
      } catch (error) {
        setExpandError("Failed to expand folder selections");
        setIsExpanding(false);
        return;
      }
      setIsExpanding(false);
    } else {
      // No folder selections and we have selectedFiles with metadata
      // Convert selectedFiles to ExpandedFileInfo format
      expandedFiles = selectedFiles.map((f) => ({
        fileId: f.id,
        fileName: f.name,
      }));
    }

    // Block save if all files are still processing
    if (expandedFiles.length === 0 && processingCount > 0) {
      setExpandError(
        "Selected documents are still being processed. Please wait for processing to complete."
      );
      return;
    }

    // Block save if no files found after expansion — unless the caller allows
    // an empty selection (the Reports template picker, where a template can
    // have no files). An empty expansion from a selected-but-empty folder is
    // still an intentional empty result in that mode.
    if (expandedFiles.length === 0 && !allowEmptySelection) {
      setExpandError("No files found in selected folders");
      return;
    }

    // Use advanced callback if provided, otherwise simple callback
    // Await in case the callback is async (e.g., creating a workspace before navigating)
    if (onConfirmWithSelection) {
      await onConfirmWithSelection({
        expandedFiles,
        selectedFileIds,
        selectedFiles,
        selectedFolderSelections: folderSelections,
      });
    } else {
      // For backward compatibility, extract just the IDs
      await onConfirm?.(expandedFiles.map((f) => f.fileId));
    }
    onClose();
  };

  // Enter confirms the selection (#10630). Listening on the document rather
  // than the panel is deliberate: the tree's checkboxes are non-focusable
  // divs, so checking a file blurs the search field and leaves focus on
  // <body> — a panel-level onKeyDown would miss the exact flow this fixes
  // (check files, hit Enter). Unlike ConfirmationModal, this modal is a
  // hand-rolled overlay with no focus trap to keep focus inside it.
  const panelRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef(handleConfirm);
  confirmRef.current = handleConfirm;
  const canConfirmRef = useRef(false);
  canConfirmRef.current = !isConfirmDisabled && !pendingUploadFiles;

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter" || !canConfirmRef.current) return;
      const target = event.target as HTMLElement | null;
      // Only our own keys: either from inside the panel, or from <body> after
      // focus was dropped by clicking a non-focusable row. A target in some
      // other overlay stacked above us is not ours to act on. The <body> arm
      // assumes nothing without a focus trap ever stacks above this modal —
      // anything that did would drop focus to <body> too and be indistinguishable.
      const panel = panelRef.current;
      if (
        target !== document.body &&
        !(panel && target && panel.contains(target))
      )
        return;
      // Leave Enter to the fields that own it, so users can type freely.
      const tag = target?.tagName;
      if (
        tag === "TEXTAREA" ||
        target?.getAttribute("contenteditable") === "true"
      )
        return;
      if (tag === "INPUT") {
        const input = target as HTMLInputElement;
        const ownsEnter =
          input.type === "search" ||
          input.type === "text" ||
          input.type === "email";
        // An empty text field still confirms — that's where focus starts, and
        // Enter on a modal is expected to do the modal's action.
        if (ownsEnter && input.value.trim() !== "") return;
      }
      event.preventDefault();
      void confirmRef.current();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  const handleBackdropMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    mouseDownTargetRef.current = event.target;
  };

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      event.target === event.currentTarget &&
      mouseDownTargetRef.current === event.currentTarget
    ) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Show drive selection modal if there are pending upload files
  if (pendingUploadFiles) {
    return (
      <DriveSelectionModal
        isOpen={true}
        onClose={handleDriveSelectionBack}
        onUploadStarting={handleDriveUploadStarting}
        onUploadComplete={handleDriveSelectionComplete}
        onUploadFailed={onUploadFailed}
        files={pendingUploadFiles}
        drives={drives}
        canUpload={canUpload}
        reuseDuplicateUploads={reuseDuplicateUploads}
      />
    );
  }

  // One Upload action (Claude Design, round 5): the modal's own, in the
  // footer on a desktop and in the header's overflow on a phone.
  // Two dialogs behind one Upload (Claude Design, round 10): files, or a
  // folder whose structure the upload keeps.
  const uploadInput = enableUpload ? (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        multiple={true}
        style={{ display: "none" }}
        disabled={!canUpload}
      />
      <input
        type="file"
        ref={folderInputRef}
        onChange={handleFileInputChange}
        multiple={true}
        webkitdirectory=""
        directory=""
        style={{ display: "none" }}
        disabled={!canUpload}
      />
    </>
  ) : null;
  // The design menu closes itself before it selects, so neither needs to.
  const uploadFiles = () => {
    if (canUpload) fileInputRef.current?.click();
  };
  const uploadFolder = () => {
    if (canUpload) folderInputRef.current?.click();
  };
  // A menu button, not a split button: nothing says files are the common
  // case from a picker. Icon + word and no caret (round 13, Claude Design):
  // a pop-up whose paper lands on its own trigger carries no disclosure
  // mark. The paper covers the button, so the button holds its pressed
  // state while open, or on dismiss it would look never touched.
  const uploadMenuOpen = uploadMenu !== null;
  const uploadButton = enableUpload ? (
    <>
      <Tooltip
        title={canUpload ? "" : "Your team is not permitted to upload files"}
        placement="top"
        arrow
      >
        <span>
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={uploadMenuOpen}
            onClick={(e) =>
              setUploadMenu({ anchor: e.currentTarget, focus: "first" })
            }
            onKeyDown={(e) => {
              if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
              e.preventDefault();
              setUploadMenu({
                anchor: e.currentTarget,
                focus: e.key === "ArrowUp" ? "last" : "first",
              });
            }}
            disabled={!canUpload}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
              uploadMenuOpen
                ? "border-gray-400 bg-gray-100"
                : "border-gray-300 bg-white hover:bg-gray-50"
            }`}
          >
            <UploadSimpleIcon size={16} aria-hidden />
            Upload
          </button>
        </span>
      </Tooltip>
      {/* The design system's menu, as on the Documents page (round 11,
          standing rule); it opens over its trigger (round 13). */}
      <Menu
        open={uploadMenuOpen}
        anchorEl={uploadMenu?.anchor ?? null}
        onClose={() => setUploadMenu(null)}
        placement="over"
        initialFocus={uploadMenu?.focus ?? "first"}
        label="Upload"
        items={[
          { label: "Upload files", onSelect: uploadFiles },
          { label: "Upload folder", onSelect: uploadFolder },
        ]}
      />
    </>
  ) : null;

  // The trail is always there (Claude Design, round 8): "All documents" at
  // the root, the folder trail inside one, in a row of fixed height, so the
  // chips row never moves on the first drill. A named view lists from the
  // root, and the trail says so.
  // Three kinds of place on the trail: a folder, the picks ("Selected", from
  // the strip's Show selected), or a record chosen from the search, which
  // carries its icon (round 10). The root crumb returns to browsing.
  const trailPath = showSelectedOnly
    ? ["Selected"]
    : onRecordView
      ? [record.isError ? "Record not found" : (record.data?.name ?? "")]
      : findState.view === "all"
        ? findState.path
        : [];
  const trailIcon =
    !showSelectedOnly && onRecordView && record.data ? (
      <span data-testid="record-crumb-icon" className="inline-flex shrink-0">
        <EntityIcon
          kind={recordIconKind(record.data.kind)}
          size={14}
          aria-hidden
        />
      </span>
    ) : undefined;
  const openRecord = (publicId: string) => {
    setShowSelectedOnly(false);
    // The only way into a record here is a search hit, and those stand
    // down inside a record, so this is always the pre-record state.
    beforeRecordRef.current = findState;
    setFindState(openedRecord(findState, publicId));
  };
  const leaveTo = (path: string[]) => {
    setShowSelectedOnly(false);
    const before = beforeRecordRef.current;
    if (onRecordView && path.length === 0 && before !== null) {
      beforeRecordRef.current = null;
      setFindState({ ...before, path: [], record: null, focus: null });
      return;
    }
    setFindState((prev) => openedFolder(prev, path));
  };
  const countText = [
    `${selectedFileCount} document${selectedFileCount !== 1 ? "s" : ""}`,
    ...(selectedFolderCount > 0
      ? [`${selectedFolderCount} folder${selectedFolderCount !== 1 ? "s" : ""}`]
      : []),
  ].join(" · ");

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={handleBackdropMouseDown}
      onClick={handleBackdropClick}
    >
      {/* A phone takes the whole screen as a sheet (Claude Design, round 5);
          a desktop keeps the caller's width and height. The type stack is the
          modal's own (round 8): inherited from the host, the same picker read
          as two products on two pages. */}
      <div
        ref={panelRef}
        // Not aria-modal: the overflow menu and the tooltips portal to the
        // body, and aria-modal would hide them from assistive technology.
        role="dialog"
        aria-label={title}
        style={{ fontFamily: "var(--fp-font-ui)" }}
        className={`bg-white ${
          phone
            ? "fixed inset-0 h-[100dvh] w-full rounded-none"
            : `rounded-lg ${widthClass} ${heightClass}`
        } flex flex-col min-w-0`}
      >
        {/* Header: the title, the trail beneath it, Close. */}
        <div
          className={`${phone ? "px-4" : "px-6"} py-3 border-b border-gray-200 flex flex-col gap-1`}
        >
          <div
            data-testid="file-selection-header"
            className="flex items-center justify-between gap-3"
          >
            <div className="flex-1 flex items-center gap-4 min-w-0">
              {titleSlot ?? (
                <h2 className="text-xl font-semibold text-gray-900 truncate">
                  {title}
                </h2>
              )}
              {headerSlot}
            </div>
            {phone && enableUpload && (
              <>
                {/* The items disable without a word of their own; the
                    button explains the missing permission instead, and the
                    menu-trigger tooltip does not flash back under a still
                    cursor when the menu closes. */}
                <MenuTriggerTooltip
                  surfaceOpen={moreAnchor !== null}
                  title={
                    canUpload
                      ? ""
                      : "Your team is not permitted to upload files"
                  }
                  placement="left"
                >
                  <button
                    type="button"
                    aria-label="More"
                    aria-haspopup="menu"
                    aria-expanded={moreAnchor !== null}
                    onClick={(e) => setMoreAnchor(e.currentTarget)}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                  >
                    <DotsThreeIcon size={24} weight="bold" aria-hidden />
                  </button>
                </MenuTriggerTooltip>
                <Menu
                  open={moreAnchor !== null}
                  anchorEl={moreAnchor}
                  onClose={() => setMoreAnchor(null)}
                  label="More"
                  items={[
                    {
                      label: "Upload files",
                      disabled: !canUpload,
                      onSelect: uploadFiles,
                    },
                    {
                      label: "Upload folder",
                      disabled: !canUpload,
                      onSelect: uploadFolder,
                    },
                  ]}
                />
              </>
            )}
            {!phone && (
              <div
                ref={setPinnedHost}
                className="flex shrink-0 items-center gap-3"
              />
            )}
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="flex h-6 min-w-0 items-center gap-2">
            {showLocationChip && !showSelectedOnly && !onRecordView && (
              <LocationChip label={locationLabel} items={locationItems} />
            )}
            {/* The chip is the trail's root when it shows (round 18h): the
                trail then reads only the folders beneath the place, so no
                name appears twice. */}
            <Breadcrumbs
              path={trailPath}
              base={showSelectedOnly || onRecordView ? [] : currentPlace.root}
              rootLabel={
                atPlacesRoot || currentPlace.kind === "tree"
                  ? "All documents"
                  : currentPlace.label
              }
              hideRoot={showLocationChip && !showSelectedOnly && !onRecordView}
              onNavigate={leaveTo}
              lastIcon={trailIcon}
            />
          </div>
        </div>
        {uploadInput}

        {/* Content. Vertically scrollable so that on short viewports (#11045)
            the fixed-height chrome above and below the listing scrolls away
            instead of crushing the listing below its minimum height. min-w-0
            the whole way down (#11742, CD (b)). */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col min-h-0 min-w-0">
          {beforeTreeSlot && (
            <div className={phone ? "px-4 pt-3" : "px-6 pt-4"}>
              {beforeTreeSlot}
            </div>
          )}
          {/* The modal is the frame (Claude Design, round 8): the listing has
              no border, radius or background of its own. */}
          <div
            data-testid="file-selection-listing"
            className="flex-1 min-h-[26rem] flex flex-col overflow-hidden"
          >
            <DocumentsBrowser
              state={findState}
              onStateChange={setFindState}
              mode="picker"
              places={places.data}
              chipSet="picker"
              phoneSheet={phone}
              excludeBlobKinds={excludeBlobKinds}
              rmsEnabled={rmsEnabled}
              selection={findSelection}
              onOpenFile={onFileClick ? openFindFile : undefined}
              pinnedHost={pinnedHost}
              onOpenRecord={(target) => openRecord(target.publicId)}
              // Not on the phone sheet, which mounts a search field of its
              // own above the chips, and not where a soft keyboard would
              // rise with the focus.
              autoFocusSearch={
                autoFocusSearch && isOpen && !phone && !hasSoftKeyboard()
              }
              recordId={onRecordView ? findState.record : null}
              awaitingRecord={awaitingRecord}
              onlyFileIds={showSelectedOnly ? selectedFileIds : undefined}
              selectedScope={{
                count: selectedCount,
                active: showSelectedOnly,
                onChange: setShowSelectedOnly,
                folders: selectedFolderEntries,
              }}
            />
          </div>
          {afterTreeSlot && (
            <div className={phone ? "px-4 pb-3" : "px-6 pb-4"}>
              {afterTreeSlot}
            </div>
          )}
        </div>

        {/* Footer, pinned: the count on the left, the actions on the right.
            Upload sits left of Cancel on a desktop; on a phone it lives in
            the header's overflow and Select takes the width. */}
        {/* On a phone the strip takes a row of its own above the actions:
            a count, a chip, Clear all and three buttons do not share 390px. */}
        <div
          data-testid="file-selection-footer"
          className={`${
            phone
              ? "px-4 py-3 flex flex-col gap-3"
              : "px-6 py-4 flex items-center justify-between gap-3"
          } border-t border-gray-200`}
        >
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="shrink-0 text-sm text-gray-500">
              {countText} selected
            </div>
            {selectedCount > 0 && (
              <button
                type="button"
                onClick={clearSelection}
                className="shrink-0 text-[12.5px] font-medium text-[var(--fp-ink-2)] hover:text-[var(--fp-ink)]"
              >
                Clear all
              </button>
            )}
            {expandError && (
              <div className="text-sm text-red-500 truncate">{expandError}</div>
            )}
          </div>
          <div
            data-testid="file-selection-actions"
            className={`flex items-center gap-3 flex-shrink-0 ${
              phone ? "justify-end" : ""
            }`}
          >
            {footerSlot}
            {!phone && uploadButton}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            {confirmButtonSlot ? (
              confirmButtonSlot({
                onConfirm: handleConfirm,
                disabled: isConfirmDisabled,
                isExpanding,
              })
            ) : (
              <button
                onClick={handleConfirm}
                disabled={isConfirmDisabled}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-md hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {/* A host that names its own verb names its own pending
                    label too ("Starting…"); the rest read Saving…. */}
                {isExpanding
                  ? "Expanding…"
                  : isPending && confirmButtonText === DEFAULT_CONFIRM
                    ? "Saving…"
                    : confirmButtonText}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileSelectionModal;
